import React, { useState } from 'react';
import { safeUrl } from "../../utils/safeUrl";
import { FiExternalLink, FiEye, FiCheck, FiX, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { 
  StatusBadge, 
  ApplicationModals, 
  useApplicationActions, 
  EXPANDED_HIDDEN_FIELDS, 
  DetailRow,
  baseUrl,
  ContentPreview,
  getVerifiedUsername,
  getVerifiedProfileUrl,
  openProfilePopup
} from './ApplicationShared';

const UserApplicationsTable = ({ applications, onActionComplete }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const actions = useApplicationActions(onActionComplete);

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    setExpandedDetail(null);
    setExpandedLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Not authenticated'); setExpandedLoading(false); return; }
      const res = await fetch(`${baseUrl}/creator-hub/application/details/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok && result) {
        setExpandedDetail(result.data ?? result);
      } else {
        toast.error(result?.message || 'Failed to load details');
      }
    } catch {
      toast.error('Network error while fetching details');
    } finally {
      setExpandedLoading(false);
    }
  };

  if (!applications || applications.length === 0) {
    return <p className="text-sm text-gray-400">No applications found.</p>;
  }

  return (
    <div className="overflow-x-auto -mx-6">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
            <th className="px-6 py-2.5">ID</th>
            <th className="px-6 py-2.5">Title</th>
            <th className="px-6 py-2.5">Type</th>
            <th className="px-6 py-2.5">URL</th>
            <th className="px-6 py-2.5">Status</th>
            <th className="px-6 py-2.5">Milestones</th>
            <th className="px-6 py-2.5 text-center">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {applications.map((app) => {
            const id = app.id ?? app._id;
            const isApproved = String(app.status || '').toLowerCase() === 'approved';
            const isRejected = ['rejected', 'reject'].includes(String(app.status || '').toLowerCase());

            return (
              <React.Fragment key={id}>
                <tr 
                  onClick={() => toggleExpand(id)}
                  className={`hover:bg-gray-50 transition cursor-pointer ${expandedId === id ? 'bg-blue-50/40' : ''}`}
                >
                  <td className="px-6 py-2.5 text-gray-700">{id}</td>
                  <td className="px-6 py-2.5 text-gray-700 max-w-[160px]">
                    <span className="block truncate">{app.title ?? '—'}</span>
                  </td>
                  <td className="px-6 py-2.5 text-gray-700 capitalize">{app.type ?? '—'}</td>
                  <td className="px-6 py-2.5 text-gray-700" onClick={(e) => e.stopPropagation()}>
                    {app.url ? (
                      <a href={safeUrl(app.url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        Link <FiExternalLink size={11} />
                      </a>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-2.5"><StatusBadge status={app.status} /></td>
                  <td className="px-6 py-2.5 text-gray-700">
                    {Array.isArray(app.milestones) ? app.milestones.length : 0}
                  </td>
                  <td className="px-6 py-2.5">
                    <div className="flex items-center gap-1.5 justify-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); actions.openDetail(id); }}
                        title="View Details"
                        className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                      >
                        <FiEye size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); actions.setModal({ type: 'approve', id }); actions.setModalUrl(app.url || ''); }}
                        title="Approve"
                        disabled={isApproved}
                        className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <FiCheck size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); actions.setModal({ type: 'reject', id }); actions.setModalMessage(''); actions.setSelectedReasonTitle(''); }}
                        title="Reject"
                        disabled={isRejected}
                        className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <FiX size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); actions.setHistoryModalOpen(true); actions.fetchHistory(id); }}
                        title="Milestone History"
                        className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition"
                      >
                        <FiClock size={14} />
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded Row */}
                {expandedId === id && (
                  <tr>
                    <td colSpan={7} className="px-6 py-5 bg-gray-50/60 border-t border-gray-100">
                      {expandedLoading ? (
                        <div className="flex justify-center py-8">
                          <div className="w-6 h-6 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : expandedDetail ? (
                        <div className="space-y-5">
                          <div className="flex items-center">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-4">Application Details</div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); actions.setModal({ type: 'approve', id }); actions.setModalUrl(app.url || ''); }}
                                disabled={isApproved}
                                className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-100 px-3 py-1.5 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <FiCheck size={12} /> Approve
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); actions.setModal({ type: 'reject', id }); actions.setModalMessage(''); actions.setSelectedReasonTitle(''); }}
                                disabled={isRejected}
                                className="flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 px-3 py-1.5 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <FiX size={12} /> Reject
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-x-6 gap-y-4">
                            {Object.entries(expandedDetail)
                              .filter(([key]) =>
                                !key.includes('Verification') &&
                                key !== 'profileVerificationData' &&
                                !EXPANDED_HIDDEN_FIELDS.includes(key.toLowerCase())
                              )
                              .map(([k, v]) => (
                                <DetailRow
                                  key={k}
                                  label={k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                                  value={v === null ? null : String(v)}
                                  link={k.toLowerCase().includes('url') || k.toLowerCase().includes('link')}
                                />
                              ))}
                              {getVerifiedUsername(expandedDetail) && (
                                <div>
                                  <DetailRow label="Verified Platform Username" value={`@${getVerifiedUsername(expandedDetail)}`} />
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openProfilePopup(getVerifiedProfileUrl(expandedDetail)); }} 
                                    className="mt-2 text-[11px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 px-2.5 py-1 rounded-md transition flex items-center gap-1 w-max"
                                  >
                                    View Profile <FiExternalLink size={10} />
                                  </button>
                                </div>
                              )}
                          </div>
                          {expandedDetail.url && (
                            <ContentPreview url={expandedDetail.url} detail={expandedDetail} />
                          )}

                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No details available.</p>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <ApplicationModals {...actions} />
    </div>
  );
};

export default UserApplicationsTable;
